import { Typography } from '@euler/app/components/typography';
import { CardNotice } from '@euler/app/flows/ai-suite/components/ChatCard';
import { PrimaryButton } from '@euler/app/flows/ai-suite/components/PrimaryButton';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiAnswerAction, BiConfirmUploadAnswer } from '@euler/model/bi/answer';
import { BiDatasetColumnRole, BiDatasetSchema } from '@euler/model/bi/dataset';
import { BiSheetUploadView } from '@euler/model/bi/upload';
import { memo, useState } from 'react';
import { View } from 'react-native';
import { useBiTap } from '../../bi-visualization/functions/useBiTap';
import {
  biColumnOverrides,
  biColumnsComplete,
  editBiColumn,
} from '../functions/biColumnEdits';
import { ColumnEditor } from './ColumnEditor';
import { ColumnRow } from './ColumnRow';
import { DescriptionField } from './DescriptionField';

/**
 * What the AI understood of one sheet: a sentence about it and a line per
 * column. Anything wrong is fixed in place; **Confirm** sends only what
 * changed, against the generation it was read at.
 */
export const ColumnConfirm = memo(
  ({
    upload,
    schema,
  }: {
    upload: BiSheetUploadView;
    schema: BiDatasetSchema;
  }) => {
    const { rpx } = useResponsiveContext();
    const { pending, busy, tap } = useBiTap();
    const [columns, setColumns] = useState(schema.columns);
    const [description, setDescription] = useState(schema.description);
    const [editing, setEditing] = useState<string | null>(null);
    // A refused confirmation leaves the corrections to send again.
    const disabled = busy || !!pending;
    const complete = biColumnsComplete(columns);
    const confirm = () => {
      const text = description.trim();
      const answer: BiConfirmUploadAnswer = {
        action: BiAnswerAction.ConfirmUpload,
        uploadId: upload.id,
        generation: upload.generation,
        overrides: biColumnOverrides(schema.columns, columns),
        ...(text && text !== schema.description ? { description: text } : {}),
      };
      if (tap(answer, upload.sheet)) setEditing(null);
    };
    return (
      <View style={{ gap: rpx(10) }}>
        <Typography.Text
          size={rpx(13)}
          lineHeight={rpx(20)}
          color={aiColors.foreground}
        >
          我是这样理解这份数据的，有不对的地方直接改，确认后再导入。
        </Typography.Text>
        <DescriptionField
          value={description}
          disabled={disabled}
          onChange={setDescription}
        />
        <View
          style={{
            borderRadius: rpx(11),
            borderWidth: 1,
            borderColor: aiColors.border,
            overflow: 'hidden',
          }}
        >
          {columns.map((column, index) => (
            <View
              key={column.key}
              style={
                index
                  ? { borderTopWidth: 1, borderTopColor: aiColors.border }
                  : undefined
              }
            >
              <ColumnRow
                column={column}
                primaryTime={
                  schema.primaryTime === column.key &&
                  column.role === BiDatasetColumnRole.Time
                }
                editing={editing === column.key}
                disabled={disabled}
                onToggle={() =>
                  setEditing(current =>
                    current === column.key ? null : column.key,
                  )
                }
              />
              {editing === column.key && !disabled ? (
                <ColumnEditor
                  column={column}
                  onChange={change =>
                    setColumns(current =>
                      current.map(entry =>
                        entry.key === column.key
                          ? editBiColumn(entry, change)
                          : entry,
                      ),
                    )
                  }
                />
              ) : null}
            </View>
          ))}
        </View>
        {!complete ? (
          <CardNotice tone="danger" text="每一列都需要名称。" />
        ) : null}
        <PrimaryButton
          title="确认并导入"
          loading={!!pending}
          disabled={disabled || !complete}
          onPress={confirm}
        />
      </View>
    );
  },
);
